import { useCallback, useEffect, useState } from 'react'
import Cropper, { Area } from 'react-easy-crop'
import { Box, Button, Slider } from '@mui/material'
import { useSnackbar } from 'notistack'

import Api from '../../utils/api'
import BasicDialog from '../m3/basic-dialog'

import DropzoneOverlay from './dropzone-overlay'
import { getCroppedImg } from './helpers'
import classes from './index.styles'

interface IProps {
  deviceId: number | undefined
  open: boolean
  onClose: () => void
  onSave: (path: string) => void
  uploadedPhoto: string | null
  isDragActive: boolean
}

const PhotoUpload = ({ deviceId, open, onClose, onSave, uploadedPhoto, isDragActive }: IProps) => {
  const { enqueueSnackbar } = useSnackbar()
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const resetState = useCallback(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }, [])

  useEffect(() => {
    if (!open) {
      setTimeout(() => resetState(), 200) // Timeout prevents flicker when closing modal
    }
  }, [open, resetState])

  const handleSubmit = async () => {
    if (!uploadedPhoto || !croppedAreaPixels) return

    try {
      setIsUploading(true)
      const croppedBlob = await getCroppedImg(uploadedPhoto, croppedAreaPixels)
      const croppedImageUrl = URL.createObjectURL(croppedBlob)

      // Slim devices during activation don't have an ID yet as the node has not been created, so we can't upload the photo until after submission. For now just close the modal and save the cropped photo in state to be uploaded after activation.
      if (!deviceId) {
        onSave(croppedImageUrl)
        resetState()
        return
      }

      const formData = new FormData()
      formData.append('photo', croppedBlob, 'photo.jpg')
      const response = await Api.put(`/api/v2/devices/${deviceId}/photo`, formData)

      if (response.ok) {
        // Convert the cropped blob to an object URL for display in thumbnail
        // Note: The parent component is responsible for revoking this URL to prevent memory leaks
        onSave(croppedImageUrl)
        resetState()
      } else {
        enqueueSnackbar('Error uploading photo. Please try again.', { variant: 'error' })
      }
    } catch {
      enqueueSnackbar('Error uploading photo. Please try again.', { variant: 'error' })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <>
      <DropzoneOverlay isDragActive={isDragActive} />
      <BasicDialog
        open={open}
        title="Device Photo"
        onClose={onClose}
        onConfirm={handleSubmit}
        confirmButton={
          <Button variant="contained" onClick={handleSubmit} loading={isUploading} disabled={isUploading}>
            Save
          </Button>
        }
        textContent={
          <>
            <Box sx={classes.cropperContainer}>
              {uploadedPhoto && (
                <Cropper
                  image={uploadedPhoto}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, croppedArea) => setCroppedAreaPixels(croppedArea)}
                />
              )}
            </Box>

            <Slider value={zoom} min={1} max={3} step={0.1} onChange={(_, z) => setZoom(z as number)} disabled={isUploading} />
          </>
        }
      />
    </>
  )
}

export default PhotoUpload
